// src/Pages/Announcements/AnnouncementBox.jsx
//
// "Tell us what's happening". The one place an owner types to Revfer.
// Submits, polls the announcement until it is read, then says what was
// understood. No confirmation step: the owner reviews the DRAFTS it produces.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, Button, Chip, Stack, Typography, Alert, CircularProgress } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { useSubmitAnnouncementMutation, useGetAnnouncementQuery } from '../../redux/thunks/announcements/announcements';
import { AnnouncementItems, GENERATION_LIVE, PENDING } from './announcementFormat';

const MAX_CHARS  = 1000;
const POLL_MS    = 2000;
const GIVE_UP_MS = 90000;

const EXAMPLES = [
  'Closing early today at 3pm',
  '20% off everything this weekend',
  'New menu starts this Friday',
  'We just hired a new team member',
];

export default function AnnouncementBox({ businessId, compact = false }) {
  const navigate = useNavigate();
  const [text, setText]         = useState('');
  const [activeId, setActiveId] = useState(null);
  const [polling, setPolling]   = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [error, setError]       = useState('');

  const [submit, { isLoading: submitting }] = useSubmitAnnouncementMutation();
  // currentData, not data: RTK keeps `data` from the previous arguments, so
  // after a reset, or on a second send, the old result would still show and
  // would stop polling for the new one. The id check makes it impossible to
  // act on another announcement's result.
  const { currentData } = useGetAnnouncementQuery(
    { businessId, announcementId: activeId },
    { skip: !activeId, pollingInterval: polling ? POLL_MS : 0 }
  );
  const ann = activeId && currentData?.announcement?.announcementId === activeId
    ? currentData.announcement
    : null;

  // Stop polling once the announcement has been read.
  useEffect(() => {
    if (ann && !PENDING.includes(ann.status)) setPolling(false);
  }, [ann]);

  // And give up after a while, rather than spin forever.
  useEffect(() => {
    if (!polling) return undefined;
    const t = setTimeout(() => { setPolling(false); setTimedOut(true); }, GIVE_UP_MS);
    return () => clearTimeout(t);
  }, [polling]);

  const handleSubmit = async () => {
    setError(''); setTimedOut(false);
    try {
      const res = await submit({ businessId, text: text.trim() }).unwrap();
      setActiveId(res.announcementId);
      setPolling(true);
      setText('');
    } catch (e) {
      setError(e?.data?.message || 'Could not send that. Please try again.');
    }
  };

  const reset = () => { setActiveId(null); setPolling(false); setTimedOut(false); };
  const reading = submitting || polling;

  return (
    <Box>
      {!compact && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Closures, offers, events, news: tell us in your own words and we'll take it from there.
        </Typography>
      )}

      <TextField
        fullWidth multiline minRows={compact ? 2 : 3} maxRows={8}
        placeholder="What's happening at your business?"
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, MAX_CHARS))}
        disabled={reading}
        size={compact ? 'small' : 'medium'}
      />

      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 1, gap: 0.5 }}>
        {EXAMPLES.map(ex => (
          <Chip key={ex} label={ex} size="small" variant="outlined" clickable
                disabled={reading} onClick={() => setText(ex)}
                sx={{ fontSize: '0.7rem', height: 22 }} />
        ))}
      </Stack>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
        <Button variant="contained" size={compact ? 'small' : 'medium'}
                startIcon={reading ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
                disabled={reading || text.trim().length < 3}
                onClick={handleSubmit}>
          {reading ? 'Reading…' : 'Send'}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {text.length}/{MAX_CHARS}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

      {timedOut && (
        <Alert severity="info" sx={{ mt: 1.5 }} onClose={reset}>
          This is taking longer than usual. It's saved; check back in a minute.
        </Alert>
      )}

      {ann && !PENDING.includes(ann.status) && (
        <Box sx={{ mt: 1.5 }}>
          {ann.status === 'interpreted' && (
            <Alert severity="success" onClose={reset}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Got it. Here's what we understood:</Typography>
              <AnnouncementItems items={ann.items.filter(i => i.kind !== 'unsupported')} dense={compact} />
              <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                {GENERATION_LIVE
                  ? "We're writing posts now. They'll be ready for review in Drafts shortly."
                  : "Saved. Turning updates into posts is coming soon."}
              </Typography>
              {GENERATION_LIVE && (
                <Button size="small" sx={{ mt: 0.5, px: 0 }} onClick={() => navigate(`/business/${businessId}/drafts`)}>
                  Go to Drafts
                </Button>
              )}
            </Alert>
          )}
          {ann.status === 'nothing_found' && (
            <Alert severity="info" onClose={reset}>
              We didn't find anything to post in that. Try telling us about a closure, an offer, an event or some news.
            </Alert>
          )}
          {ann.status === 'unsupported' && (
            <Alert severity="warning" onClose={reset}>
              That isn't something we can post on your behalf.
            </Alert>
          )}
          {ann.status === 'failed' && (
            <Alert severity="error" onClose={reset}>
              Something went wrong reading that. It's saved, and you can try sending it again.
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
}